import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  FiCheck,
  FiArrowRight,
  FiMonitor,
  FiServer,
  FiCode,
  FiClock,
  FiPackage,
  FiBarChart2,
  FiHelpCircle
} from 'react-icons/fi';
import SEO from '../components/SEO';
import './Pricing.css';

const AnimatedSection = ({ children, className, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Pricing = () => {
  const [activeTooltip, setActiveTooltip] = useState(null);

  const toggleTooltip = (id) => {
    setActiveTooltip(activeTooltip === id ? null : id);
  };

  const templates = [
    {
      name: 'Apartment Templates',
      price: '299',
      description: 'Modern designs for multifamily communities',
      features: [
        'Floor plans section',
        'Amenities gallery',
        'Contact form integration',
        'Mobile responsive design',
        'Source code included'
      ],
      note: 'Does not include API setup for rental payment or tenant application services. Integration available at additional charge.'
    },
    {
      name: 'Law Firm Templates',
      price: '299',
      priceNote: '– $349',
      description: 'Professional templates for legal practices',
      features: [
        'Attorney profiles',
        'Practice areas pages',
        'Case results section',
        'FAQ accordion',
        'Source code included'
      ],
      popular: true
    },
    {
      name: 'Real Estate Templates',
      price: '299',
      priceNote: '– $399',
      description: 'Residential and commercial property templates',
      features: [
        'Property listings',
        'Agent/broker profiles',
        'Mortgage calculator',
        'Advanced animations',
        'Source code included'
      ]
    }
  ];

  const addons = [
    {
      name: 'Full Setup Service',
      price: '$499',
      description: 'We customize and launch your site for you. Custom branding, content updates, domain setup, and SSL certificate.',
      oneTime: true
    },
    {
      name: 'Analytics Setup',
      price: '$299',
      description: 'Google Analytics 4 + Search Console setup, conversion tracking, and ROI reporting dashboard.',
      oneTime: true
    }
  ];

  const hostingFeatures = [
    'Secure cloud hosting on Render',
    'SSL certificate management',
    'Uptime monitoring',
    'Security updates & maintenance',
    'DNS management',
    'Troubleshooting & support',
    'Regular backups'
  ];

  const customServices = [
    {
      icon: <FiCode />,
      name: 'Custom Development',
      description: 'Full-stack web applications, e-commerce platforms, booking systems, and more.',
      price: 'Starting at $2,500'
    },
    {
      icon: <FiMonitor />,
      name: 'Website Redesign',
      description: 'Modernize your existing website with a fresh design and improved UX.',
      price: 'Starting at $1,500'
    },
    {
      icon: <FiClock />,
      name: 'Hourly Development',
      description: 'New pages, feature additions, major edits, and ongoing development work.',
      price: '$125 / hour'
    }
  ];

  return (
    <>
      <SEO
        title="Pricing"
        description="Transparent pricing for website templates, hosting, maintenance, and custom development services. No hidden fees."
        url="/pricing"
      />

      {/* Hero Section */}
      <section className="pricing-hero">
        <div className="pricing-hero__bg">
          <div className="pricing-hero__gradient-orb pricing-hero__gradient-orb--1"></div>
          <div className="pricing-hero__gradient-orb pricing-hero__gradient-orb--2"></div>
          <div className="pricing-hero__grid-overlay"></div>
        </div>
        <div className="container">
          <motion.div
            className="pricing-hero__content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="pricing-hero__label">Transparent Pricing</span>
            <h1 className="pricing-hero__title">Pricing</h1>
            <p className="pricing-hero__subtitle">
              Clear, straightforward pricing with no hidden costs. Choose the solution that fits your business needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Templates Section */}
      <section className="pricing-templates section">
        <div className="container">
          <AnimatedSection className="pricing-section-header">
            <span className="pricing-section-label">Website Templates</span>
            <h2>Ready-to-Launch Templates</h2>
            <p>Professional, mobile-responsive templates for specific industries. Get online fast with a polished, modern website.</p>
          </AnimatedSection>

          <div className="pricing-templates__grid">
            {templates.map((template, index) => (
              <AnimatedSection key={template.name} delay={index * 0.1}>
                <div className={`pricing-template-card ${template.popular ? 'pricing-template-card--popular' : ''}`}>
                  {template.popular && <span className="pricing-template-card__badge">Most Popular</span>}
                  <h3 className="pricing-template-card__name">
                    {template.name}
                    {template.note && (
                      <span className="pricing-template-card__info-wrapper">
                        <button
                          className="pricing-template-card__info-btn"
                          onClick={() => toggleTooltip(template.name)}
                          aria-label="More information"
                        >
                          <FiHelpCircle />
                        </button>
                        {activeTooltip === template.name && (
                          <span className="pricing-template-card__tooltip">
                            {template.note}
                          </span>
                        )}
                      </span>
                    )}
                  </h3>
                  <div className="pricing-template-card__price">
                    <span className="pricing-template-card__currency">$</span>
                    <span className="pricing-template-card__amount">{template.price}</span>
                    {template.priceNote && <span className="pricing-template-card__price-note">{template.priceNote}</span>}
                  </div>
                  <p className="pricing-template-card__description">{template.description}</p>
                  <ul className="pricing-template-card__features">
                    {template.features.map((feature, i) => (
                      <li key={i}>
                        <FiCheck />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/templates" className="pricing-template-card__btn">
                    View Templates
                    <FiArrowRight />
                  </Link>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* Add-ons Section */}
          <AnimatedSection className="pricing-addons" delay={0.3}>
            <div className="pricing-addons__header">
              <h3>Optional Add-ons</h3>
              <p>Enhance your template purchase with professional services</p>
            </div>
            <div className="pricing-addons__grid">
              <div className="pricing-addon-card">
                <div className="pricing-addon-card__icon">
                  <FiPackage />
                </div>
                <div className="pricing-addon-card__content">
                  <h4>Full Setup Service</h4>
                  <p>We customize and launch your site for you. Custom branding, content updates, domain setup, and SSL certificate.</p>
                </div>
                <div className="pricing-addon-card__price">
                  <span className="pricing-addon-card__amount">+$499</span>
                  <span className="pricing-addon-card__note">one-time</span>
                </div>
              </div>
              <div className="pricing-addon-card">
                <div className="pricing-addon-card__icon">
                  <FiBarChart2 />
                </div>
                <div className="pricing-addon-card__content">
                  <h4>Analytics Setup</h4>
                  <p>Google Analytics 4 + Search Console setup, conversion tracking, and ROI reporting dashboard.</p>
                </div>
                <div className="pricing-addon-card__price">
                  <span className="pricing-addon-card__amount">+$299</span>
                  <span className="pricing-addon-card__note">one-time</span>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Hosting Section */}
      <section className="pricing-hosting section">
        <div className="container">
          <div className="pricing-hosting__grid">
            <AnimatedSection className="pricing-hosting__content">
              <span className="pricing-section-label">Monthly Service</span>
              <h2>Hosting & Maintenance</h2>
              <p>
                Let us handle the technical details so you can focus on your business. Our managed hosting includes everything you need to keep your site running smoothly.
              </p>
              <ul className="pricing-hosting__features">
                {hostingFeatures.map((feature, index) => (
                  <li key={index}>
                    <FiCheck />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
            <AnimatedSection delay={0.2} className="pricing-hosting__card-wrapper">
              <div className="pricing-hosting__card">
                <div className="pricing-hosting__card-icon">
                  <FiServer />
                </div>
                <h3>Managed Hosting</h3>
                <div className="pricing-hosting__price">
                  <span className="pricing-hosting__currency">$</span>
                  <span className="pricing-hosting__amount">100</span>
                  <span className="pricing-hosting__period">/ month</span>
                </div>
                <p>Complete hosting and maintenance for your website</p>
                <Link to="/contact" className="btn-glow">
                  Get Started
                  <FiArrowRight />
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Custom Development Section */}
      <section className="pricing-custom section">
        <div className="container">
          <AnimatedSection className="pricing-section-header">
            <span className="pricing-section-label">Custom Services</span>
            <h2>Development & Design</h2>
            <p>Need something custom? We build tailored solutions to meet your specific business requirements.</p>
          </AnimatedSection>

          <div className="pricing-custom__grid">
            {customServices.map((service, index) => (
              <AnimatedSection key={service.name} delay={index * 0.1}>
                <div className="pricing-custom-card">
                  <div className="pricing-custom-card__icon">
                    {service.icon}
                  </div>
                  <h3 className="pricing-custom-card__name">{service.name}</h3>
                  <p className="pricing-custom-card__description">{service.description}</p>
                  <span className="pricing-custom-card__price">{service.price}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pricing-cta">
        <div className="pricing-cta__bg">
          <div className="pricing-cta__gradient"></div>
        </div>
        <div className="container">
          <AnimatedSection className="pricing-cta__content">
            <h2>Ready to Get Started?</h2>
            <p>Let's discuss your project and find the right solution for your business.</p>
            <div className="pricing-cta__buttons">
              <Link to="/contact" className="btn-glow btn-glow--large">
                Contact Us
                <FiArrowRight />
              </Link>
              <a href="tel:9728008105" className="btn-outline-dark">
                Call (972) 800-8105
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};

export default Pricing;
